import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material"
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    type SxProps,
} from "@mui/material"
import { type ReactNode } from "react"
import { intl } from "src/internationalization/intl"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import { Typography } from "../../basics"
import {
    barColorForRatio,
    clientStatusColor,
    displayRatioForAdvisor,
    displayRatioForPortfolio,
    formatClientsDone,
    formatPercent,
    isClientExecuted,
    type AdvisorChartPortfolio,
    type AdvisorChartRow,
    type MonitoringMetricMode,
} from "./AdvisorExecutionChart"

const sortPortfolios = (
    portfolios: AdvisorChartPortfolio[],
    direction: "desc" | "asc",
    metricMode: MonitoringMetricMode,
): AdvisorChartPortfolio[] =>
    [...portfolios].sort((a, b) => {
        const ra = displayRatioForPortfolio(a, metricMode)
        const rb = displayRatioForPortfolio(b, metricMode)
        const na = ra === null
        const nb = rb === null
        if (na && nb) return a.label.localeCompare(b.label, "pt-BR")
        if (na) return 1
        if (nb) return -1
        const cmp = ra! - rb!
        return direction === "desc" ? -cmp : cmp
    })

const headerCell = (label: string, align: "left" | "right" = "left") => (
    <TableCell align={align} sx={{ whiteSpace: "nowrap" }}>
        <Typography variant="caption" component="span" sx={{ color: "neutral.lighter", fontWeight: 700 }}>
            {label}
        </Typography>
    </TableCell>
)

const bodyCell = (content: ReactNode, align: "left" | "right" = "left", maxWidth?: number) => (
    <TableCell align={align} sx={{ whiteSpace: "nowrap", maxWidth }}>
        <Typography variant="body2" component="span">
            {content}
        </Typography>
    </TableCell>
)

/* ------------------------------------------------------------------ */
/* ESQUERDA — tabela por assessor (selecionável)                       */
/* ------------------------------------------------------------------ */

export interface AdvisorExecutionTableProps {
    rows: AdvisorChartRow[]
    selectedKeys: string[]
    onToggle: (groupKey: string) => void
    metricMode: MonitoringMetricMode
    tableSx: SxProps
    tableContainerSx: SxProps
}

const AdvisorExecutionTable = ({
    rows,
    selectedKeys,
    onToggle,
    metricMode,
    tableSx,
    tableContainerSx,
}: AdvisorExecutionTableProps) => {
    if (!rows.length) {
        return (
            <Typography variant="caption" color="secondary">
                {intl("objectiveMonitoringChartEmpty")}
            </Typography>
        )
    }
    return (
        <TableContainer sx={tableContainerSx}>
            <Table size="small" sx={tableSx}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 36 }} />
                        {headerCell(intl("objectiveMonitoringAdvisorCol"))}
                        {headerCell(intl("objectiveMonitoringOpportunityShort"), "right")}
                        {headerCell(intl("objectiveMonitoringClientsCol"), "right")}
                        {headerCell(intl("objectiveMonitoringAmountCol"), "right")}
                        {headerCell(intl("objectiveMonitoringPercentDoneCol"), "right")}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const selected = selectedKeys.includes(row.groupKey)
                        const ratio = displayRatioForAdvisor(row, metricMode)
                        return (
                            <TableRow
                                key={row.groupKey}
                                hover
                                onClick={() => onToggle(row.groupKey)}
                                sx={{
                                    cursor: "pointer",
                                    "& .MuiTableCell-root": selected
                                        ? { backgroundColor: "neutral.darker !important" }
                                        : undefined,
                                }}
                            >
                                <TableCell sx={{ width: 36 }}>
                                    {selected ? (
                                        <CheckCircle sx={{ fontSize: 18, color: "primary.main", display: "block" }} />
                                    ) : (
                                        <RadioButtonUnchecked sx={{ fontSize: 18, color: "neutral.dark", display: "block" }} />
                                    )}
                                </TableCell>
                                {bodyCell(row.advisorLabel || intl("objectiveMonitoringNoAdvisor"), "left", 200)}
                                {bodyCell(formatCurrencyBRL(row.opportunitySum), "right")}
                                {bodyCell(formatClientsDone(row.clientsDone, row.clientsTotal), "right")}
                                {bodyCell(formatCurrencyBRL(row.liquidoSum), "right")}
                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{ fontWeight: 700, color: barColorForRatio(ratio) }}
                                    >
                                        {formatPercent(ratio)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

/* ------------------------------------------------------------------ */
/* DIREITA — drill-down das carteiras dos assessores selecionados      */
/* ------------------------------------------------------------------ */

const PortfolioBar = ({
    p,
    metricMode,
}: {
    p: AdvisorChartPortfolio
    metricMode: MonitoringMetricMode
}) => {
    const ratio = displayRatioForPortfolio(p, metricMode)
    const fillPct = ratio === null ? 0 : Math.max(0, Math.min(1, ratio)) * 100
    const done = isClientExecuted(p.percentRatio)
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
            <Box sx={{ width: 120, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <Typography variant="caption" noWrap title={p.label}>
                    {p.label}
                </Typography>
            </Box>
            <Typography
                variant="caption"
                sx={{
                    width: 72,
                    flexShrink: 0,
                    fontWeight: 700,
                    color: clientStatusColor(done),
                    textAlign: "center",
                }}
            >
                {done ? intl("objectiveMonitoringClientDone") : intl("objectiveMonitoringClientNotDone")}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 48,
                    height: 14,
                    borderRadius: 1,
                    bgcolor: "neutral.darkest",
                    border: "1px solid rgba(255, 255, 255, 0.06)",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ width: `${fillPct}%`, height: "100%", bgcolor: barColorForRatio(ratio) }} />
            </Box>
            <Box sx={{ width: 54, flexShrink: 0, textAlign: "right" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: barColorForRatio(ratio) }}>
                    {metricMode === "clients"
                        ? done
                            ? intl("objectiveMonitoringClientDone")
                            : intl("objectiveMonitoringClientNotDone")
                        : formatPercent(ratio)}
                </Typography>
            </Box>
        </Box>
    )
}

export interface AdvisorPortfolioDrilldownProps {
    advisors: AdvisorChartRow[]
    viewMode: "chart" | "table"
    metricMode: MonitoringMetricMode
    sortDirection: "desc" | "asc"
    tableSx: SxProps
    tableContainerSx: SxProps
}

const AdvisorPortfolioDrilldown = ({
    advisors,
    viewMode,
    metricMode,
    sortDirection,
    tableSx,
    tableContainerSx,
}: AdvisorPortfolioDrilldownProps) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {advisors.map((advisor) => {
                const ports = sortPortfolios(advisor.portfolios, sortDirection, metricMode)
                const advisorRatio = displayRatioForAdvisor(advisor, metricMode)
                return (
                    <Box key={advisor.groupKey}>
                        {/* Cabeçalho do assessor */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                                gap: 1,
                                mb: 0.75,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                    {advisor.advisorLabel || intl("objectiveMonitoringNoAdvisor")}
                                </Typography>
                                <Typography variant="caption" color="quaternary">
                                    {metricMode === "volume"
                                        ? `${formatCurrencyBRL(advisor.liquidoSum)} / ${formatCurrencyBRL(advisor.opportunitySum)}`
                                        : formatClientsDone(advisor.clientsDone, advisor.clientsTotal)}
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: barColorForRatio(advisorRatio), flexShrink: 0 }}
                            >
                                {formatPercent(advisorRatio)}
                            </Typography>
                        </Box>

                        {viewMode === "chart" ? (
                            <Box>
                                {ports.map((p, idx) => (
                                    <PortfolioBar key={`${p.account}-${idx}`} p={p} metricMode={metricMode} />
                                ))}
                            </Box>
                        ) : (
                            <TableContainer sx={tableContainerSx}>
                                <Table size="small" sx={tableSx}>
                                    <TableHead>
                                        <TableRow>
                                            {headerCell(intl("objectiveMonitoringPortfolioCol"))}
                                            {headerCell(intl("objectiveMonitoringOpportunityShort"), "right")}
                                            {headerCell(intl("objectiveMonitoringClientStatusCol"), "right")}
                                            {headerCell(intl("objectiveMonitoringAmountCol"), "right")}
                                            {headerCell(intl("objectiveMonitoringPercentDoneCol"), "right")}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ports.map((p, idx) => {
                                            const done = isClientExecuted(p.percentRatio)
                                            const ratio = displayRatioForPortfolio(p, metricMode)
                                            return (
                                            <TableRow key={`${p.account}-${idx}`}>
                                                {bodyCell(p.label, "left", 200)}
                                                {bodyCell(
                                                    p.opportunityVolume !== null
                                                        ? formatCurrencyBRL(p.opportunityVolume)
                                                        : intl("objectiveMonitoringNA"),
                                                    "right",
                                                )}
                                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                    <Typography
                                                        variant="body2"
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: clientStatusColor(done),
                                                        }}
                                                    >
                                                        {done
                                                            ? intl("objectiveMonitoringClientDone")
                                                            : intl("objectiveMonitoringClientNotDone")}
                                                    </Typography>
                                                </TableCell>
                                                {bodyCell(formatCurrencyBRL(p.executedValue), "right")}
                                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                    <Typography
                                                        variant="body2"
                                                        component="span"
                                                        sx={{ fontWeight: 700, color: barColorForRatio(ratio) }}
                                                    >
                                                        {metricMode === "clients"
                                                            ? done
                                                                ? intl("objectiveMonitoringClientDone")
                                                                : intl("objectiveMonitoringClientNotDone")
                                                            : formatPercent(ratio)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )
            })}
        </Box>
    )
}

export { AdvisorExecutionTable, AdvisorPortfolioDrilldown }
